import { useState, useEffect } from "react";
import {
  Calendar,
  Pencil,
  Trash2,
  MapPin,
  Users,
  DollarSign,
} from "lucide-react";
import EditEventForm from "./EditEventForm";
import { successToast, errorToast } from "../../utils/toasters";
import {
  updatePersonalEvent,
  updateCommercialEvent,
  updateCommunityEvent,
  deleteCommercialEvent,
  deletePersonalEvent,
  deleteCommunityEvent,
} from "../../services/events";
function ManageEvents({ events: initialEvents }) {
  const [editingEvent, setEditingEvent] = useState(null);
  const [localEvents, setLocalEvents] = useState(initialEvents || []);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const handleEdit = (event) => {
    setEditingEvent(event);
  };

  const handleUpdate = async (eventId, changes) => {
    if (changes.category === "personal") {
      const { category, ...filteredChanges } = changes;

      const fieldMap = {
        eventName: "eventname",
        date: "eventstartdate",
        description: "eventdescription",
        venue: "venue",
        guests: "guestcount",
      };

      const mapKeys = (obj, map) =>
        Object.fromEntries(
          Object.entries(obj).map(([key, val]) => [map[key] || key, val])
        );

      const mappedChanges = mapKeys(filteredChanges, fieldMap);
      const updatedEvent = { id: eventId, ...mappedChanges };

      try {
        const response = await updatePersonalEvent(updatedEvent);
        if (response.status === "success" || response.status === "sucess") {
          setLocalEvents((prevEvents) =>
            prevEvents.map((event) =>
              event.id === eventId
                ? {
                    ...event,
                    ...filteredChanges,
                    ...(filteredChanges.guests !== undefined && {
                      guestCount: filteredChanges.guests,
                      guests: undefined,
                    }),
                  }
                : event
            )
          );

          successToast("Event updated successfully", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        } else {
          throw new Error(response.error || "Update failed");
        }
      } catch (error) {
        errorToast("Failed to update event. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      }
    } else if (changes.category === "commercial") {
      const { category, ...filteredChanges } = changes;

      const fieldMap = {
        eventName: "name",
        date: "date",
        description: "description",
        venue: "venue",
        city: "city",
        state: "state",
        startTime: "eventstarttime",
        endTime: "eventendtime",
      };
      const mapKeys = (obj, map) =>
        Object.fromEntries(
          Object.entries(obj).map(([key, val]) => [map[key] || key, val])
        );

      const mappedChanges = mapKeys(filteredChanges, fieldMap);
      const updatedEvent = { id: eventId, ...mappedChanges };
      try {
        const response = await updateCommercialEvent(eventId, updatedEvent);
        if (response.status === "success" || response.status === "sucess") {
          setLocalEvents((prevEvents) =>
            prevEvents.map((event) =>
              event.id === eventId ? { ...event, ...filteredChanges } : event
            )
          );

          successToast("Event updated successfully", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        } else {
          throw new Error(response.error || "Update failed");
        }
      } catch (error) {
        errorToast("Failed to update event. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      }
    } else if (changes.category === "community") {
      const { category, ...filteredChanges } = changes;

      const fieldMap = {
        eventName: "name",
        date: "date",
        description: "description",
        venue: "venue",
        city: "city",
        state: "state",
        startTime: "eventstarttime",
        endTime: "eventendtime",
      };
      const mapKeys = (obj, map) =>
        Object.fromEntries(
          Object.entries(obj).map(([key, val]) => [map[key] || key, val])
        );

      const mappedChanges = mapKeys(filteredChanges, fieldMap);
      const updatedEvent = { id: eventId, ...mappedChanges };
      try {
        const response = await updateCommunityEvent(eventId, updatedEvent);
        if (response.status === "success" || response.status === "sucess") {
          setLocalEvents((prevEvents) =>
            prevEvents.map((event) =>
              event.id === eventId ? { ...event, ...filteredChanges } : event
            )
          );

          successToast("Event updated successfully", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        } else {
          throw new Error(response.error || "Update failed");
        }
      } catch (error) {
        errorToast("Failed to update event. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      }
    }
  };
  const handleDeleteEvent = async (eventId, category) => {
    const originalEvents = [...localEvents];
    try {
      setLocalEvents((prevEvents) =>
        prevEvents.filter((event) => event.id !== eventId)
      );
      let response;
      if (category === "personal") {
        response = await deletePersonalEvent(eventId);
      } else if (category === "commercial") {
        response = await deleteCommercialEvent(eventId);
      } else if (category === "community") {
        response = await deleteCommunityEvent(eventId);
      } else {
        throw new Error("Invalid event category");
      }
      if (response.status !== "success" && response.status !== "sucess") {
        throw new Error(response.error || "Delete failed");
      }

      successToast("Event deleted successfully", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
      });
    } catch (error) {
      setLocalEvents(originalEvents);
      errorToast("Failed to delete event. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
      });
    } finally {
      setIsDeleteConfirmOpen(false);
      setEventToDelete(null);
    }
  };

  const handleDeleteClick = (event) => {
    setEventToDelete(event);
    setIsDeleteConfirmOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false);
    setEventToDelete(null);
  };

  const handleTrackRequest = (eventId) => {
    // Handle track request functionality
  };

  const formatEventTime = (event) => {
    if (event.startTime && event.endTime) {
      return `${event.startTime} - ${event.endTime}`;
    } else if (event.date) {
      const time = event.date.split("T")[1]?.slice(0, 8);
      return time || "";
    }
    return "";
  };

  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      <div className="rounded-lg border border-gray-100 bg-white p-4 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
          Manage Events
        </h1>

        <div className="w-full">
          {/* Show empty state if no events */}
          {!localEvents || localEvents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="mb-2">You don't have any events yet.</p>
              <p className="text-sm">Create your first event to get started!</p>
            </div>
          ) : (
            <>
              {/* Desktop Header - Hidden on mobile */}
              <div className="hidden md:grid md:grid-cols-4 bg-gray-100 p-3 rounded-t-md">
                <div className="text-sm text-gray-600">Event Type</div>
                <div className="text-sm text-gray-600">Event Details</div>
                <div className="text-sm text-gray-600">Date & Time</div>
                <div className="text-sm text-gray-600 text-right">Actions</div>
              </div>

              {/* Event rows */}
              {localEvents.map((event) => (
                <div
                  key={event.id}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  {/* Mobile Layout */}
                  <div className="md:hidden p-4 space-y-3">
                    {/* Event header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-base font-semibold truncate">
                            {event.eventName || event.name}
                          </h3>
                        </div>
                        <span className="inline-block text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded mb-2">
                          {event.eventType || event.name}
                        </span>
                      </div>
                    </div>

                    {/* Event details */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center text-gray-600">
                        <Calendar size={14} className="mr-2 flex-shrink-0" />
                        <span className="truncate">
                          {formatEventDate(event.date)}
                          {formatEventTime(event) &&
                            ` at ${formatEventTime(event)}`}
                        </span>
                      </div>

                      <div className="flex items-center text-gray-600 text-sm mb-1">
                        <MapPin size={14} className="mr-1" />
                        <span className="truncate">
                          {event.venue ? event.venue : event.location}
                        </span>
                      </div>

                      {event.eventType === "Personal Events" && (
                        <div className="flex items-center gap-3 text-gray-600 text-xs">
                          {event.guests && (
                            <div className="flex items-center">
                              <Users size={12} className="mr-1" />
                              <span>{event.guests} guests</span>
                            </div>
                          )}
                          {event.budget && (
                            <div className="flex items-center">
                              <DollarSign size={12} className="mr-1" />
                              <span>${event.budget}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {event.eventType === "Personal Events" &&
                        event.needs?.length > 0 && (
                          <div className="text-gray-600 text-xs">
                            <span className="font-medium">Needs:</span>{" "}
                            {event.needs.join(", ")}
                          </div>
                        )}
                    </div>

                    {/* Mobile Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <button
                        onClick={() => handleTrackRequest(event.id)}
                        className="text-[#7B189F] hover:text-[#6A1587] font-medium text-sm transition-colors"
                      >
                        Track Request
                      </button>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(event)}
                          className="flex items-center gap-1 px-2 py-2 rounded-full border border-gray-300 text-[#7B189F] hover:bg-gray-50 transition-colors"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(event)}
                          className="text-gray-500 hover:text-red-500 p-2 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden md:grid md:grid-cols-4 py-4 items-center">
                    {/* Event Type column */}
                    <div className="flex items-center">
                      <span className="inline-block text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {event.eventType || event.name}
                      </span>
                    </div>

                    {/* Event Details column */}
                    <div>
                      <h3 className="font-medium mb-1">
                        {event.eventName || event.name}
                      </h3>

                      <div className="flex items-center text-gray-600 text-sm mb-1">
                        <MapPin size={14} className="mr-1" />
                        <span className="truncate">
                          {event.venue ? event.venue : event.location}
                        </span>
                      </div>

                      {event.eventType === "Personal Events" && (
                        <div className="flex items-center gap-3 text-gray-600 text-sm">
                          {event.guests && (
                            <div className="flex items-center">
                              <Users size={14} className="mr-1" />
                              <span>{event.guests} guests</span>
                            </div>
                          )}
                          {event.budget && (
                            <div className="flex items-center">
                              <DollarSign size={14} className="mr-1" />
                              <span>${event.budget}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {event.eventType === "Personal Events" &&
                        event.needs?.length > 0 && (
                          <div className="text-gray-600 text-sm mt-1">
                            <span className="font-medium">Needs:</span>{" "}
                            {event.needs.slice(0, 2).join(", ")}
                            {event.needs.length > 2 && "..."}
                          </div>
                        )}
                    </div>

                    {/* Date & Time column */}
                    <div className="text-sm">
                      <div className="flex items-center text-gray-600 mb-1">
                        <Calendar size={14} className="mr-1" />
                        <span>{formatEventDate(event.date)}</span>
                      </div>
                      {formatEventTime(event) && (
                        <div className="text-gray-500 text-xs ml-5">
                          {formatEventTime(event)}
                        </div>
                      )}
                    </div>

                    {/* Actions column */}
                    <div className="flex justify-end items-center gap-2">
                      <button
                        onClick={() => handleTrackRequest(event.id)}
                        className="text-[#7B189F] hover:text-[#6A1587] font-medium text-sm transition-colors"
                      >
                        Track Request
                      </button>
                      <div className="w-px h-4 bg-gray-200" />
                      <button
                        onClick={() => handleEdit(event)}
                        className="flex items-center gap-1 px-2 py-2 rounded-full border border-gray-300 text-[#7B189F] hover:bg-gray-50 transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(event)}
                        className="text-gray-500 hover:text-red-500 p-2 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Edit Event Modal */}
      {editingEvent && (
        <EditEventForm
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onUpdate={handleUpdate}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleCancelDelete}
            />

            {/* Dialog */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Delete Event
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to delete? This action cannot be undone.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleCancelDelete}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() =>
                      handleDeleteEvent(
                        eventToDelete?.id,
                        eventToDelete?.category
                      )
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ManageEvents;
